import FolderBagSvg from "@/src/assets/icons/icon-fold-bag.svg";
import Icon from "@/src/components/atom/Icon";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import Typo from "@/src/components/atom/Typo";
import TabItem from "@/src/components/molecule/TabItem";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import { getFormattedTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import { useGetTermsQuery } from "@/src/store/apis/common";
import { TermsForAnonymousDto } from "@/src/store/apis/common/interface";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { utcNow } from "@/src/utils/utcDayjs";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

const termsTabsData = {
  termsOfUse: "Terms of Use",
  privacyPolicy: "Privacy Policy",
};

// Reverse mapping: titleEn -> query param key
const titleEnToTabKey: Record<string, string> = {
  "Terms of Use": "termsOfUse",
  "Privacy Policy": "privacyPolicy",
};

const TermsPage = () => {
  const [searchParam, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<number>();
  const [selectedTermId, setSelectedTermId] = useState<number>();

  const selectTabQuery = searchParam.get("selectTab");
  const defaultTabQuery = searchParam.get("defaultTab");
  const isSelectTabQuery = !!selectTabQuery;

  const { isFetching, currentData, isSuccess, isError } = useGetTermsQuery(
    {
      service: "EXPORT_WEB",
      titleEn: isSelectTabQuery
        ? termsTabsData[selectTabQuery as keyof typeof termsTabsData]
        : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const isValidTerms = useMemo(() => {
    return (
      currentData
        ?.filter(({ isActivated, isOptional }) => !isOptional && isActivated)
        .reduce<TermsForAnonymousDto[]>((acc, val) => {
          return [
            ...acc,
            {
              ...val,
              termsHistories: val.termsHistories
                .filter(
                  ({ releasedAt }) =>
                    dayjs.utc(releasedAt).isBefore(utcNow(), "day") ||
                    dayjs.utc(releasedAt).isSame(utcNow(), "day")
                )
                .sort((a, b) => b.releasedAt.localeCompare(a.releasedAt)),
            },
          ];
        }, []) || []
    );
  }, [currentData]);

  const selectedTerm = isValidTerms?.find(({ id }) => id === selectedTab);

  const handleTabChange = (termDetail: TermsForAnonymousDto) => {
    setSelectedTab(termDetail.id);
    setSelectedTermId(termDetail.currentTermsHistory.id);

    // Update URL to reflect selected tab
    const tabKey = titleEnToTabKey[termDetail.titleEn];
    if (tabKey) {
      setSearchParams({ defaultTab: tabKey }, { replace: true });
    }
  };

  useEffect(() => {
    if (!isFetching && isSuccess && isValidTerms.length) {
      const isValidDefaultTab = defaultTabQuery && defaultTabQuery in termsTabsData;
      const defaultTitleEn = isValidDefaultTab
        ? termsTabsData[defaultTabQuery as keyof typeof termsTabsData]
        : null;
      const defaultTerm = defaultTitleEn
        ? isValidTerms.find((term) => term.titleEn === defaultTitleEn)
        : null;
      const initialTerm = defaultTerm || isValidTerms[0];

      setSelectedTab(initialTerm.id);
      setSelectedTermId(initialTerm.currentTermsHistory.id);
    }
  }, [isFetching, isSuccess, isValidTerms, defaultTabQuery, selectTabQuery]);

  return (
    <Layout>
      <PublicPageHeader />

      <MainSection>
        <ContentWrapper>
          <TitleSection>
            <Typo as="h1" typoType="d3" color="gray1">
              {t("terms:label.legal")}
            </Typo>
          </TitleSection>

          {isFetching ? (
            <WaitingSection>
              <Loader size={50} />
            </WaitingSection>
          ) : isError ? (
            <PlaceholderSection>
              <Icon
                iconSrc={FolderBagSvg}
                iconSize={120}
                className="flex-shrink-0"
              />
              <Typo typoType="b7m" color="gray8">
                {t("terms:label.noData")}
              </Typo>
            </PlaceholderSection>
          ) : (
            <>
              {!isSelectTabQuery && (
                <Tabs role="tablist">
                  {isValidTerms.map((term) => {
                    return (
                      <StyledTab
                        itemListCount={isValidTerms.length}
                        tabValue={term.id}
                        tabIndex={term.id === selectedTab ? 0 : -1}
                        onClick={() => handleTabChange(term)}
                        onFocus={() => handleTabChange(term)}
                        data-selected={term.id === selectedTab}
                      >
                        {i18n.language === "ko" ? term.titleKo : term.titleEn}
                      </StyledTab>
                    );
                  })}
                </Tabs>
              )}

              <Section>
                <SpaceBetween>
                  <Typo typoType="h4">
                    {i18n.language === "ko"
                      ? selectedTerm?.titleKo
                      : selectedTerm?.titleEn}
                  </Typo>

                  <Select
                    style={{ width: 210 }}
                    value={selectedTermId}
                    onChange={(value) => setSelectedTermId(value)}
                    options={selectedTerm?.termsHistories.map(
                      ({ releasedAt, id }) => ({
                        label: `${getFormattedTimezoneDateWithOffset({ date: releasedAt, gmtOffset: 9 })} (KST)`,
                        value: id,
                      })
                    )}
                  />
                </SpaceBetween>

                <ContentContainer>
                  <div
                    className="ck ck-content"
                    dangerouslySetInnerHTML={{
                      __html:
                        i18n.language === "ko"
                          ? selectedTerm?.termsHistories.find(
                              ({ id }) => id === selectedTermId
                            )?.bodyKo || "-"
                          : selectedTerm?.termsHistories.find(
                              ({ id }) => id === selectedTermId
                            )?.bodyEn || "-",
                    }}
                  />
                </ContentContainer>
              </Section>
            </>
          )}
        </ContentWrapper>
      </MainSection>

      <PublicPageFooter />
    </Layout>
  );
};

export default TermsPage;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: ${colorSet.white};
`;

const MainSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  padding: 60px 40px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 0;

  h1 {
    text-align: center;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1250px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Tabs = styled.div`
  display: flex;
  width: 100%;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
`;

const StyledTab = styled(TabItem)<{ itemListCount: number }>`
  text-align: center;
  padding: 8px 16px;
  border: 1px solid ${colorSet.gray9};
  background: ${colorSet.gray11};
  color: ${colorSet.gray6};
  ${typo.b7m}
  cursor: pointer;
  width: ${({ itemListCount }) => `calc(${100 / itemListCount}%)`};

  &[data-selected="true"] {
    background: ${colorSet.indigo};
    color: ${colorSet.white};
  }

  &:first-of-type {
    border-radius: 8px 0px 0px 8px;
  }

  &:last-of-type {
    border-radius: 0px 8px 8px 0px;
  }
`;

const Section = styled.section`
  padding: 24px;
  width: 100%;
`;

const SpaceBetween = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border-bottom: 1px solid ${colorSet.gray8};
  margin-bottom: 16px;
`;

const ContentContainer = styled.div`
  padding: 16px;

  * {
    all: revert;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
    line-height: normal;
  }

  ul {
    list-style: none;
    padding-left: 1em;
  }
`;

const WaitingSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 300px);
  width: 100%;
`;

const PlaceholderSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 300px);
  width: 100%;

  padding: 40px;

  .flex-shrink-0 {
    flex-shrink: 0;
  }
`;
